/**
 * The original's **situational** callouts — the half of `RB.EXE`'s message set
 * `sim-messages.ts` never wired (G33 / completeness-inventory M8).
 *
 * Every string in this file is verbatim from the `RB.EXE` string dump, and
 * `docs/game/ui-menus.md` § *In-flight HUD / status messages* is the
 * transcription. Nothing here invents copy: where the original has no string for
 * a state, this file has no message for it either.
 *
 * ## Determinism
 *
 * Every callout is a **pure function of two consecutive states** — the same
 * shape `derivedMessages` already used for the two fuel/altitude warnings. There
 * is no "already said this" flag anywhere: a cue fires on the tick its predicate
 * crosses, and a replay of the same tape produces the same lines in the same
 * order. Nothing here writes to sim state, so `SNAPSHOT_VERSION` and the
 * encounter hash are untouched — these functions read the state the engine
 * publishes and return strings.
 *
 * ## The two evidence classes, kept apart
 *
 * - **The words** are recovered. Every constant below cites its `RB.EXE` line.
 * - **The triggers** are not. `ui-menus.md` is explicit that the exact trigger
 *   conditions are *"inferred from wording, not confirmed by play-testing"*.
 *   Each threshold below therefore names itself and says it is port-side. The
 *   one exception is `ARCHIE SPOTTED`, whose predicate was built and left
 *   unwired: `combat/flak-battery.ts`'s `underFlakFire` carries the docstring
 *   *"what a ARCHIE SPOTTED cue keys off"*.
 */

import {
  contactReport,
  nationOf,
  SEA_LEVEL_DENSITY_KGM3,
  stallSpeedMps,
  type AircraftKey,
  type CombatAircraft,
  type EncounterState,
  type MissionRun,
  type MissionType,
  type Nation,
  type SimState,
  type TargetKind,
} from "@red-opus/engine";

/* ------------------------------------------------------------------ *
 * The strings. All verbatim `RB.EXE`; the line numbers are its own
 * `strings -n 4` ordinals, which is how ui-menus.md cites them too.
 * ------------------------------------------------------------------ */

/** `RB.EXE` 866. Fires on a new contact. */
export const ENEMY_SPOTTED = "ENEMY AIRCRAFT SPOTTED!";
/** `RB.EXE` 867/868 — the pair, one per side of the wire. "Archie" is RFC slang for German AA. */
export const FLAK_SPOTTED: Readonly<Record<Nation, string>> = {
  allied: "GERMAN ARCHIE SPOTTED!",
  german: "BRITISH AA GUNS SPOTTED!",
};
/** `RB.EXE` 704, from `s\intel\shooting.c`. Sentence case in the original, and kept that way. */
export const BEING_SHOT_AT = "You're being shot at!";
/** `RB.EXE` 872. */
export const NOW_FLIGHT_LEADER = "YOU'RE NOW THE FLIGHT LEADER!";
/** `RB.EXE` 873. */
export const MISSION_COMPLETE = "THE MISSION IS COMPLETE.";

/**
 * The opening the four referents below are actually paired with, verbatim at
 * `RB.EXE` `0x474a9` — **including its trailing space**, which is what says it
 * is a prefix rather than a sentence. It sits in the time-compression block
 * (`0x47440`–`0x474e1`) rather than beside the referents, because in the
 * original this is the line that ends compression and hands the aeroplane back:
 * see `autopilot.ts`.
 */
export const NEAR_PREFIX = "YOU NEAR ";

/**
 * The referents at `RB.EXE` `0x4725c`–`0x4728c`, a contiguous list immediately
 * after `TIME COMPRESS DEACTIVATED.` and before the attitude callouts, composed
 * onto `NEAR_PREFIX`.
 *
 * `YOU'RE NEARING THE BOMBING TARGET.` (`0x4731b`) is the one member that is a
 * whole sentence in the binary, and it keeps its own wording — it lives with
 * the flight callouts, not with this list, and the two openings are genuinely
 * different strings in the original.
 *
 * `undefined` for the types the original never announces an approach to — a
 * patrol and a dogfight have nowhere to be nearing.
 */
export const NEARING_MESSAGES: Readonly<Partial<Record<MissionType, string>>> = {
  escortBombers: "YOU'RE NEARING THE BOMBING TARGET.",
  stopBombers: `${NEAR_PREFIX}THE BOMBERS.`,
  destroyZeppelin: `${NEAR_PREFIX}THE ZEPPELINS.`,
  interceptZeppelin: `${NEAR_PREFIX}THE ZEPPELINS.`,
  escortRecon: `${NEAR_PREFIX}THE RECON AIRCRAFT.`,
  balloonDefense: `${NEAR_PREFIX}YOUR BALLOONS.`,
};

/** `RB.EXE` 682. */
export const LOW_AMMUNITION = "You're getting low on ammunition.";
/** `RB.EXE` 693. */
export const WINGS_SHAKING = "Your wings are shaking...";
/** `RB.EXE` 694. */
export const ABOUT_TO_STALL = "You are about to stall...";
/** `RB.EXE` 696. */
export const OIL_PRESSURE = "Your oil pressure is dropping!";
/** `RB.EXE` 697. */
export const FUEL_LINES = "Your fuel lines have been hit!";
/**
 * `RB.EXE` 604–605. Two lines in the original ("After beating furiously on your
 * / guns, they have unjammed!!"), joined with a space because this port's
 * message feed is one line per entry.
 */
export const GUNS_UNJAMMED = "After beating furiously on your guns, they have unjammed!!";

/* ------------------------------------------------------------------ *
 * The thresholds. Port-side, every one, and each says what it is for.
 * ------------------------------------------------------------------ */

/**
 * How close a hostile has to be to be *spotted*. Port-side: the original's own
 * spotting distance is not recovered. 3 km is roughly where a scout stops being
 * a speck at this port's field of view, and it is comfortably outside the
 * ~500 m the fight is actually joined at, so the cue is a warning rather than a
 * commentary on a merge already in progress.
 */
export const SPOT_RANGE_M = 3000;
/** How close the mission's business has to be for the approach cue. Port-side. */
export const NEARING_RANGE_M = 4000;
/** Nose-on cone and range for "he is shooting at me". Port-side; gun range is ~500 m in this sim. */
export const SHOT_AT_CONE_RAD = Math.PI / 9;
export const SHOT_AT_RANGE_M = 600;
/** Belt fraction at which the ammunition warning fires. Port-side. */
export const LOW_AMMUNITION_FRACTION = 0.2;
/** Fraction of the airframe's own limit load at which the wings start to talk. Port-side. */
export const WING_SHAKE_FRACTION = 0.85;
/** Margin above stall speed inside which the stall warning fires. Port-side. */
export const STALL_WARNING_MARGIN = 1.1;
/** Engine health below which the oil pressure is "dropping". Port-side; the oil needle is already synthesised from `damage.engine`. */
export const OIL_PRESSURE_HEALTH = 0.75;
/** Fuel-system health below which the lines count as hit. Port-side. */
export const FUEL_LINE_HEALTH = 0.9;

/* ------------------------------------------------------------------ *
 * Kill feedback.
 * ------------------------------------------------------------------ */

/**
 * `RB.EXE` 986/987/992/993 and the class-name block at file `0x47DFE`. The
 * trailing spaces are the original's — these are `printf` prefixes, not
 * sentences (`docs/game/ground-traffic.md` §1 resolves the whole table).
 */
export const SCRATCH_ONE = "Scratch one ";
export const SHOT_DOWN = "You just shot down ";
export const OOPS = "Oops!  You just took out a ";
/**
 * Recovered (`RB.EXE` 993) and deliberately **not composed into a line**: the
 * dump shows the interjection, not what it prefixes, and every reading that
 * would pair it with one of the sentences above produces text the original may
 * never have printed. Exported so the constant is on the record and a later
 * play-test session can place it; inventing its sentence is what this file will
 * not do.
 */
export const WHOOPS = "Whoops!  ";
/** `RB.EXE` 677, `s\combat\collide.c` — the line that feeds the Board of Inquiry. */
export const SHOT_AT_ONE_OF_OURS = "You just shot at one of ours!";
/** `RB.EXE`'s own line for the airship, which has no entry in the kill-noun table. */
export const ZEPPELIN_DESTROYED = "Zeppelin destroyed!";

/**
 * `pyro.c`'s kill-noun table at `DGROUP+0xf06`, indexed by object class:
 * `{plane, –, balloon, truck, –, train}` (`ground-traffic.md` §1, every offset
 * resolved against the file).
 *
 * The two NULLs are not gaps and are not filled here: class 1 is the Zeppelin,
 * which has `Zeppelin destroyed!` instead, and class 4 is empty. This port's
 * raid structures — `supplyDump`, `factory`, `railYard` — and its `searchlight`
 * have **no recovered noun**, so they get no line, exactly as a NULL entry
 * produces none in the original.
 */
export const KILL_NOUNS: Readonly<Partial<Record<TargetKind, string>>> = {
  balloon: "balloon",
  train: "train",
};

/** Class 0's noun: what the same table calls an aeroplane. */
export const AIRCRAFT_KILL_NOUN = "plane";

/**
 * `You just shot down ` takes `German `, `French ` or `British ` (`RB.EXE`
 * 994–996). The port's own nation model is two-valued (`allied`/`german`), so
 * the Allied half is split here by the aeroplane's *manufacturer*, which is
 * plain history rather than a recovered table — and it is stated as such. Every
 * key is listed rather than defaulted, so a new aeroplane fails the totality
 * test in `sim-callouts.test.ts` instead of quietly becoming British.
 */
const ALLIED_ORIGINS: Readonly<Record<string, string>> = {
  MORANE: "French",
  NIEU11: "French",
  NIEUPORT: "French",
  SPAD_7: "French",
  SPAD_13: "French",
  NIEU28: "French",
  AIRCO: "British",
  SOPPUP: "British",
  SOPTRI: "British",
  SE5: "British",
  CAMEL: "British",
  SNIPE: "British",
  BE2: "British",
  FE2: "British",
  RE8: "British",
  BRISTOL: "British",
  DH4: "British",
  HANDLEY: "British",
};

export function nationalityWordOf(aircraft: AircraftKey): string {
  if (nationOf(aircraft) === "german") return "German";
  return ALLIED_ORIGINS[aircraft] ?? "Allied";
}

/* ------------------------------------------------------------------ *
 * Predicates. Each is a plain question about one state; the callers
 * below diff them across a tick.
 * ------------------------------------------------------------------ */

function livePlayer(state: EncounterState, playerId: string): CombatAircraft | undefined {
  const player = state.aircraft.find((entry) => entry.id === playerId);
  return player && player.status !== "destroyed" ? player : undefined;
}

function rangeM(a: { x: number; y: number; z: number }, b: { x: number; y: number; z: number }): number {
  return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
}

/** True while a live hostile aeroplane is inside spotting range of a live player. */
export function enemyInSight(state: EncounterState, playerId: string): boolean {
  const player = livePlayer(state, playerId);
  if (!player) return false;
  return state.aircraft.some(
    (entry) =>
      entry.side === "hostile" &&
      entry.status !== "destroyed" &&
      rangeM(entry.sim.position, player.sim.position) < SPOT_RANGE_M,
  );
}

/**
 * True while a live hostile has its nose on the player at gun range with the
 * trigger down — `s\intel\shooting.c`'s question, answered from the state the
 * AI already publishes (`ai.firing`) plus the geometry `contactReport` gives.
 */
export function underAimedFire(state: EncounterState, playerId: string): boolean {
  const player = livePlayer(state, playerId);
  if (!player) return false;
  return state.aircraft.some((entry) => {
    if (entry.side !== "hostile" || entry.status === "destroyed" || !entry.ai?.firing) return false;
    const report = contactReport(player.id, entry.sim, player.sim);
    return report.rangeM < SHOT_AT_RANGE_M && Math.abs(report.relativeBearingRad) < SHOT_AT_CONE_RAD;
  });
}

/**
 * True once every friendly aeroplane ahead of the player in the roster is gone.
 *
 * **A reading, and stated as one.** `ui-menus.md` gives the trigger as *"original
 * leader shot down/lost"*, and this port always seats the player as leader of his
 * own flight — so taken narrowly the cue could never fire. Roster order is what
 * the FLIGHT ASSIGNMENTS sheet prints as the order of seniority, so "everyone
 * above me is down" is the closest thing the port's own model has to the
 * original's question. On an escort it fires when the machines you were sent to
 * protect have all gone in, which is when it matters.
 */
export function seniorSurvivor(state: EncounterState, playerId: string): boolean {
  const friendly = state.aircraft.filter((entry) => entry.side === "friendly");
  const index = friendly.findIndex((entry) => entry.id === playerId);
  if (index <= 0) return false;
  return friendly.slice(0, index).every((entry) => entry.status === "destroyed");
}

/** Rounds left as a fraction of the belt this aeroplane went up with. */
export function ammunitionFraction(sim: SimState): number {
  const capacity = sim.guns.capacity;
  return capacity > 0 ? Math.min(Math.max(sim.guns.rounds / capacity, 0), 1) : 0;
}

/** How hard the wing is working, as a fraction of its own limit. `null` when the airframe carries no limit. */
export function wingLoadFraction(sim: SimState): number | null {
  const limit = sim.config.spec.structure.limitLoadFactor;
  if (limit === null || limit <= 0) return null;
  return Math.abs(sim.readout.loadFactor) / limit;
}

/**
 * True while the aeroplane is flying slowly enough to be about to stall, but has
 * not yet. `stallSpeedMps` is the engine's own 1-g figure, taken at sea-level
 * density: a warning that fires a little early high up is the right kind of
 * wrong for a warning.
 *
 * ## Why the threshold is capped at the aeroplane's own liftoff speed
 *
 * `STALL_WARNING_MARGIN` is port-side (see its own comment) and
 * `liftoffSpeedMps` is derived, and after AD-007o the two contradicted each
 * other. The wing incidence made `gearStaticPitchStallFraction` the binding
 * term in `undercarriage.ts`'s `groundPitchRad` on ten of the thirty-three
 * airframes, which pins their three-point wing angle at exactly
 * `0.85 · alphaStallRad` — and since `clMax = clAlphaPerRad · alphaStallRad`,
 * that puts their liftoff speed at exactly `1/sqrt(0.85) = 1.0847 · Vs`,
 * *below* the 1.10 the warning fires at. Every Camel, Snipe, Pfalz, Nieuport
 * and F.E.2 take-off flown the way `03-flight-reference.md` instructs ("when
 * your aircraft gets up at about 40 mph, gently pull back") therefore crossed
 * the warning band for three or four ticks on the way out of it — and
 * `Fly.tsx`'s message list holds the last three lines for the rest of the
 * sortie, so a 0.05-second transient stood on the HUD until three more
 * messages pushed it off. Measured: 0 of 33 airframes warned before the
 * incidence shipped, 10 of 33 after.
 *
 * The port must not warn a pilot for flying the manual's own take-off, so the
 * threshold is the *lower* of the two: an aeroplane at or above the speed its
 * own wing makes its weight at is not about to stall, whatever a port-side
 * margin says. On the twenty-three airframes whose liftoff speed already sits
 * above `Vs · STALL_WARNING_MARGIN` this changes nothing.
 */
export function nearStall(sim: SimState): boolean {
  if (sim.readout.stalled || sim.readout.grounded) return false;
  const stallMps = stallSpeedMps(sim.config.spec.aero, sim.config.spec.massKg, SEA_LEVEL_DENSITY_KGM3);
  if (!(stallMps > 0)) return false;
  return sim.readout.airspeedMps < stallWarningSpeedMps(sim);
}

/**
 * The speed the stall warning fires below: the port-side margin above the 1-g
 * stall, never above the speed the aeroplane legitimately leaves the ground at.
 */
export function stallWarningSpeedMps(sim: SimState): number {
  const stallMps = stallSpeedMps(sim.config.spec.aero, sim.config.spec.massKg, SEA_LEVEL_DENSITY_KGM3);
  return Math.min(stallMps * STALL_WARNING_MARGIN, sim.config.spec.undercarriage.liftoffSpeedMps);
}

/* ------------------------------------------------------------------ *
 * The approach cue's subject.
 * ------------------------------------------------------------------ */

/**
 * What each mission type's `YOU'RE NEARING …` line is *about*, resolved from the
 * mission's own plan rather than from a guess at the roster.
 *
 * Every entry reads a structure the mission already carries, so the cue cannot
 * drift from the mission: the escort's target list is the raid's own
 * `targetIds`, the interception's bombers are its `raiderIds`, the recon
 * aeroplane is whatever the `protect` objective names, and the balloons are
 * whatever `protectTargets` names. There is no entry for `patrol`, `dogfight`,
 * `scramble` or `balloonBusting` because `NEARING_MESSAGES` has no string for
 * them — the original never announces an approach on those.
 */
function nearingSubjectIds(spec: MissionRun["spec"]): { aircraft: readonly string[]; targets: readonly string[] } {
  const none = { aircraft: [] as readonly string[], targets: [] as readonly string[] };
  const objective = (kind: "protect" | "protectTargets") => spec.objectives.find((entry) => entry.kind === kind);
  switch (spec.type) {
    case "escortBombers":
      return { aircraft: [], targets: spec.raid?.targetIds ?? [] };
    case "stopBombers":
      return { aircraft: spec.raid?.raiderIds ?? [], targets: spec.raid?.raiderIds ?? [] };
    case "destroyZeppelin":
    case "interceptZeppelin":
      return { aircraft: [], targets: spec.targets.filter((t) => t.kind === "zeppelin").map((t) => t.id) };
    case "escortRecon": {
      const protectFlight = objective("protect");
      return { aircraft: protectFlight?.kind === "protect" ? protectFlight.flightIds : [], targets: [] };
    }
    case "balloonDefense": {
      const protectTargets = objective("protectTargets");
      return { aircraft: [], targets: protectTargets?.kind === "protectTargets" ? protectTargets.targetIds : [] };
    }
    default:
      return none;
  }
}

/**
 * Distance from the player to the nearest thing the mission is about, or `null`
 * when the mission has no such thing — which is most of them.
 *
 * `stopBombers` looks its raiders up in *both* halves of the roster on purpose:
 * `RaidPlan.raiderIds` may name an aeroplane or a Zeppelin, and the airship is a
 * target rather than a flight only because it has no aerodynamics of its own.
 */
export function nearingDistanceM(run: MissionRun): number | null {
  const player = livePlayer(run.encounter, run.spec.playerFlightId);
  if (!player) return null;
  const { aircraft, targets } = nearingSubjectIds(run.spec);
  const positions = [
    ...run.encounter.aircraft
      .filter((entry) => aircraft.includes(entry.id) && entry.status !== "destroyed")
      .map((entry) => entry.sim.position),
    ...run.encounter.targets
      .filter((entry) => targets.includes(entry.id) && !entry.destroyed)
      .map((entry) => entry.position),
  ];
  if (positions.length === 0) return null;
  return Math.min(...positions.map((position) => rangeM(position, player.sim.position)));
}

/** True while the player is inside the approach range of whatever the mission is about. */
export function isNearingSubject(run: MissionRun): boolean {
  const distance = nearingDistanceM(run);
  return distance !== null && distance < NEARING_RANGE_M;
}
